import { signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { CountryMarketConfig } from '../../common/CountryMarketConfig';
import { Heading } from '../../components/Heading';
import { Loading } from '../../components/Loading';
import { SSOLoginButton } from '../../components/SSOLoginButton';
import { useCountryMarketState } from '../../controllers/CountryMarketController/CountryMarketState';
import { auth, functions } from '../../firebase';
import { GTMEventData } from '../../hooks/GTMUtils';
import { getCustomers } from '../../hooks/queries';
import {
  CountryMarketHelpers,
  useCountryMarketHelpers,
} from '../../hooks/useCountryMarketHelpers';
import { useGTM } from '../../hooks/useGTM';
import {
  LocalizationCookieHandler,
  useLocalizationCookieHandler,
} from '../../hooks/useLocalizationCookieHandler';
import { User } from '../../shared/store';
import { formatWindowSizeForGTM } from '../../shared/useWindowSize';
import { CountryMarketUrl, TrackingSchema } from '../../types';

export const redirectByCountryMarketSituation = async (
  countryMarketConfig: CountryMarketConfig | null | undefined,
  userSettings: User,
  navigate: NavigateFunction,
  localizationCookieHandler: LocalizationCookieHandler,
  updateDataLayer: <T>(eventBlock: GTMEventData<T>) => void,
  {
    filterValidCountryMarkets,
    findCountryMarketConfigByString,
    redirectTargetByCountryMarket,
    redirectTargetByCountryMarketConfig,
  }: CountryMarketHelpers,
): Promise<void> => {
  const customers = await getCustomers(userSettings);
  const userTrackingData: Record<string, unknown> = {
    userGUID: userSettings.GUID,
    identStatus: userSettings.azelis_employee ? 'employee' : 'customer',
    access: true,
  };

  if (userSettings.customers.length > 1) {
    // a universal customer
    userTrackingData.assignedCustomerGUIDs = customers.map(({ GUID }) => GUID);

    userTrackingData.assignedCustomerNames = customers.map(
      ({ customer_name }) => customer_name,
    );

    userTrackingData.customerGUID = null;
    userTrackingData.customerName = null;
  } else {
    // any other user
    const customer = customers.find(
      ({ erp_id }) => erp_id === userSettings.customers[0],
    );

    userTrackingData.customerGUID = userSettings.azelis_employee
      ? null
      : customer?.GUID;
    userTrackingData.customerName = userSettings.azelis_employee
      ? null
      : customer?.customer_name;
  }

  updateDataLayer({
    event: 'User Login',
    schema: TrackingSchema.user,
    data: userTrackingData,
  });

  if (!userSettings.azelis_employee) {
    updateDataLayer({
      event: 'Impersonator Switch',
      schema: TrackingSchema.impersonator,
      data: {
        asCustomerName: 'no impersonator',
        asCustomerGUID: 'no impersonator',
        asCustomerUserGUID: 'no impersonator',
      },
    });
  }

  if (countryMarketConfig) {
    // Use the country/market from the store
    return navigate(
      redirectTargetByCountryMarketConfig(countryMarketConfig, 'start'),
    );
  }

  const systemValidCountryMarkets = filterValidCountryMarkets(
    Object.values(userSettings.countries_and_markets || {}),
  );

  if (systemValidCountryMarkets.length === 1) {
    // Default to first of the user's country/markets
    const countryMarketString = systemValidCountryMarkets[0];
    const {
      languages,
      taxonomyMarketAbbreviaton,
      datalakeCountryAbbreviation,
    } = findCountryMarketConfigByString(countryMarketString) ?? {};

    const cookieLanguage = localizationCookieHandler.getLanguageFromCookie();

    updateDataLayer({
      event: 'Update',
      schema: TrackingSchema.site,
      data: {
        language: languages?.length === 1 ? languages[0] : '',
        environment: process.env.REACT_APP_ENVIRONMENT || 'local',
        viewport: formatWindowSizeForGTM([
          window.innerWidth,
          window.innerHeight,
        ]),
        marketSegment: taxonomyMarketAbbreviaton,
        country: datalakeCountryAbbreviation,
      },
    });

    if (languages?.length === 1) {
      localizationCookieHandler.changeAppLanguage(languages[0]);

      return navigate(
        redirectTargetByCountryMarket(countryMarketString, 'start'),
      );
    } else if (languages?.includes(cookieLanguage)) {
      localizationCookieHandler.changeAppLanguage(cookieLanguage);

      return navigate(
        redirectTargetByCountryMarket(countryMarketString, 'start'),
      );
    }
  } else if (systemValidCountryMarkets.length === 0) {
    return navigate(CountryMarketUrl.NotAvailable);
  }

  navigate(CountryMarketUrl.Empty);
};

export const OauthHandler: FC = () => {
  const { t } = useTranslation();
  const [{ countryMarketConfig }] = useCountryMarketState();
  const [errorMessage, setErrorMessage] = useState<string>();
  const navigate = useNavigate();
  const localizationCookieHandler = useLocalizationCookieHandler();
  const { updateDataLayer } = useGTM();

  const countryMarketHelpers = useCountryMarketHelpers();

  useEffect(() => {
    if (!countryMarketHelpers.ready) return;
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const errorMsg = urlParams.get('error');
    if (errorMsg) {
      setErrorMessage(t(`components:signIn:errors:${errorMsg}`));
    } else if (code) {
      httpsCallable<string, { signinToken: string; userSettings: User }>(
        functions,
        'getTokenFromOAuthCode',
      )(code)
        .then(
          async ({
            data: { signinToken, userSettings },
          }: {
            data: { signinToken: string; userSettings: User };
          }) => {
            await signInWithCustomToken(auth, signinToken);

            localizationCookieHandler.changeAppLanguage();

            redirectByCountryMarketSituation(
              countryMarketConfig,
              userSettings,
              navigate,
              localizationCookieHandler,
              updateDataLayer,
              countryMarketHelpers,
            );
          },
        )
        .catch((error) => {
          // @FIXME AZEELAB-317 Report this error in a proper manner
          console.log(error);
        });
    }
    // Disable this line to stop excessive calls to getTokenFromOAuthCode AZEELAB-320
    // eslint-disable-next-line
  }, [countryMarketHelpers.ready]);

  return (
    <div className="flex flex-col items-center justify-center flex-1">
      {errorMessage ? (
        <>
          <Heading
            text={t('components:signIn:errors:errorCustomerPortalLogin')}
            align="center"
          />
          <div className="mb-4 text-error" data-testid="error-message">
            {errorMessage}
          </div>
          <SSOLoginButton />
        </>
      ) : (
        <>
          <Loading />
          <p className="mt-2" data-testid="loading-message">
            {t('components:signIn:pleaseWait')}
          </p>
        </>
      )}
    </div>
  );
};
